import { Link, LinkProps, To } from 'react-router-dom'
import { useMemo } from 'react'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import { Menu, X, ChevronRight, ChevronLeft, Sun, Moon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { NavigationItem, MobileNavigationProps, NavigationSection } from "@/types/navigation"
import { navigationItems } from '@/types/navigation'
import { isRouteEnabled } from '@/config/route-visibility'
import { useTheme } from '@/hooks/useTheme.ts'

const NavigationLink = ({ children, to, ...props }: LinkProps & { to: To }) => {
  return <Link to={to} {...props}>{children}</Link>
}

// First, let's modify the menuColors object to be type-safe and ensure exact matches
type MenuColorKey = 'Services' | 'Investment Division' | 'Software & Technology' | 'Insights' | 'Community';

const menuColors: Record<MenuColorKey, string> = {
  'Services': 'text-orange-500/70',
  'Investment Division': 'text-emerald-600',      // Lighter emerald
  'Software & Technology': 'text-purple-500',
  'Insights': 'text-sky-400',
  'Community': 'text-pink-500'
};

export function MainNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const memoizedNavigationItems = useMemo<NavigationSection[]>(() => {
    // Filter navigation based on route visibility config.
    // - A subItem is included only if its href is enabled.
    // - An item is included if its own href is enabled OR it has at least one enabled subItem.
    // - A section is included if it has at least one enabled item.
    return navigationItems
      .map((section) => {
        const filteredItems = section.items
          .map((item): NavigationItem | null => {
            const enabledSubItems = item.subItems
              ? item.subItems.filter((sub) => isRouteEnabled(String(sub.href)))
              : []

            const itemEnabled =
              isRouteEnabled(String(item.href)) || enabledSubItems.length > 0

            if (!itemEnabled) return null

            return {
              ...item,
              subItems: enabledSubItems,
            }
          })
          .filter((x): x is NavigationItem => x !== null)

        if (filteredItems.length === 0) return null

        return {
          ...section,
          items: filteredItems,
        }
      })
      .filter((x): x is NavigationSection => x !== null)
  }, [])
  const { theme, setTheme } = useTheme()
  
  // Close menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Prevent scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  const DesktopNavigation = () => {
    const [activeItem, setActiveItem] = useState<NavigationItem | null>(null)

    const handleSectionHover = (section: NavigationSection) => {
      setActiveItem(section.items?.[0] || null)
    }

    return (
      <div className="hidden md:block">
        <NavigationMenu>
          <NavigationMenuList>
            {memoizedNavigationItems.map((section) => (
              <NavigationMenuItem
                key={section.title}
                onMouseEnter={() => handleSectionHover(section)}
              >
                <NavigationMenuTrigger>{section.title}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="flex w-[800px] p-4">
                    <ul className="w-1/3 border-r pr-4">
                      {section.items.map((item) => (
                        <li key={item.title}>
                          <NavigationLink
                            to={item.href}
                            className={`block select-none rounded-md p-3 leading-none transition-colors cursor-pointer
                              ${activeItem === item
                                ? 'bg-neutral-100 dark:bg-neutral-800 text-black dark:text-white'
                                : 'hover:bg-neutral-50 dark:hover:bg-neutral-900 text-black dark:text-white'}`}
                            onMouseEnter={() => setActiveItem(item)}
                          >
                            {item.title}
                          </NavigationLink>
                        </li>
                      ))}
                    </ul>
                    <div className="w-2/3 pl-4">
                      {activeItem?.subItems && (
                        <ul className="grid grid-cols-2 gap-3">
                          {activeItem.subItems.map((subItem) => (
                            <li key={subItem.href}>
                              <NavigationLink
                                to={subItem.href}
                                className="block rounded-md p-2 hover:bg-neutral-50"
                              >
                                {subItem.label}
                              </NavigationLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>
      </div>
    )
  }

  const MobileNavigation: React.FC<MobileNavigationProps> = ({ isOpen, onClose }) => {
    // Add state to track active section for collapsible navigation
    const [activeSection, setActiveSection] = useState<string | null>(null);

    // Animation variants for transitions
    const containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          duration: 0.3,
          staggerChildren: 0.1
        }
      },
      exit: {
        opacity: 0,
        transition: { duration: 0.2 }
      }
    };

    const itemVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -20 }
    };

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={containerVariants}
            className="fixed inset-0 bg-white dark:bg-neutral-900 z-40 md:hidden overflow-y-auto"
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="sticky top-0 flex items-center justify-between p-4 border-b bg-white dark:bg-neutral-900 z-10">
                {activeSection ? (
                  <motion.button
                    onClick={() => setActiveSection(null)}
                    className="flex items-center text-neutral-600 dark:text-neutral-400"
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronLeft className="w-5 h-5 mr-1" />
                    Back
                  </motion.button>
                ) : (
                  <Link to="/" onClick={onClose} className="flex items-center">
                  
                    <div className="font-bold text-lg">Polymath</div>
                  </Link>
                )}
                <motion.button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="w-6 h-6" />
                </motion.button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {activeSection ? (
                  // Show items from the active section
                  <motion.div
                    className="space-y-4"
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                  >
                    {memoizedNavigationItems
                      .find(item => item.title === activeSection)
                      ?.items.map((subItem) => (
                        <motion.div key={subItem.title} className="mb-6" variants={itemVariants}>
                          <NavigationLink
                            to={subItem.href}
                            className="block font-medium text-lg mb-3 p-3 rounded-lg border hover:bg-neutral-50 dark:hover:bg-neutral-800"
                            onClick={onClose}
                          >
                            <div>{subItem.title}</div>
                            <div className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                              {subItem.description}
                            </div>
                          </NavigationLink>

                          {subItem.subItems && subItem.subItems.length > 0 && (
                            <motion.div
                              className="pl-4 border-l border-neutral-200 dark:border-neutral-700 ml-2"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              transition={{ duration: 0.3 }}
                            >
                              {subItem.subItems.map((nestedItem) => (
                                <NavigationLink
                                  key={nestedItem.href}
                                  to={nestedItem.href}
                                  className="block py-2 px-3 text-sm text-neutral-600 dark:text-neutral-400 hover:text-black dark:hover:text-white"
                                  onClick={onClose}
                                >
                                  {nestedItem.label}
                                </NavigationLink>
                              ))}
                            </motion.div>
                          )}
                        </motion.div>
                      ))}
                  </motion.div>
                ) : (
                  // Show main sections
                  <motion.div
                    className="space-y-4"
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                  >
                    {memoizedNavigationItems.map((item) => (
                      <motion.button
                        key={item.title}
                        onClick={() => setActiveSection(item.title)}
                        className="flex items-center justify-between w-full p-4 text-left rounded-lg border hover:bg-neutral-50 dark:hover:bg-neutral-800"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        variants={itemVariants}
                        transition={{ duration: 0.2 }}
                      >
                        <span className={`text-lg font-medium ${menuColors[item.title as MenuColorKey] || ''}`}>
                          {item.title}
                        </span>
                        <ChevronRight className="w-5 h-5 text-neutral-400" />
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t space-y-4">
                {/* Theme Toggle */}
                <div className="flex justify-between items-center p-3 rounded-lg border">
                  <span className="text-sm font-medium">Toggle Theme</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                    className="h-8 w-8"
                  >
                    {theme === 'dark' ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* About Us Link */}
                <Link
                  to="/about"
                  onClick={onClose}
                  className="flex justify-between items-center p-3 rounded-lg border hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  <span className="text-sm font-medium">About Us</span>
                  <ChevronRight className="h-4 w-4" />
                </Link>

                {/* Contact and Login Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Link to="/contact" onClick={onClose} className="text-center py-2 px-4 border rounded-md">
                    Contact Us
                  </Link>
                  <Link to="/investor-login" onClick={onClose} className="text-center py-2 px-4 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-md">
                    Investor Login
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <div className="relative">
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden fixed top-4 right-4 z-50"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        {isMenuOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <Menu className="h-6 w-6" />
        )}
      </Button>

      {/* Mobile Menu */}
      <MobileNavigation isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

      {/* Desktop Navigation */}
      <DesktopNavigation />
    </div>
  )
} 